/* eslint-disable @typescript-eslint/no-explicit-any */
import { Barcode } from '@bofrak-backend/shared';
import { getErrorMessage, productDb } from '@bofrak-backend/shared-ui';
import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Tag,
  TagLabel,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaBarcode,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaLock,
  FaPen,
  FaPlus,
  FaSave,
  FaTimes,
} from 'react-icons/fa';

// ─── Barcode type inference ────────────────────────────────────────────────────

const PURE_DIGITS = /^\d+$/;
const UPC_A = /^\d{12}$/;
const UPC_E = /^\d{8}$/;
const EAN_13 = /^\d{13}$/;
const EAN_8 = /^\d{8}$/;
const ITF_14 = /^\d{14}$/;
const ISBN_13 = /^(978|979)\d{10}$/;
const ISBN_10 = /^\d{9}[\dXx]$/;
const CODE_39 = /^[A-Z0-9\-. $/+%]+$/;

function inferBarcodeType(value: string): string {
  const v = value.trim();
  if (!v) return 'unknown';

  if (EAN_13.test(v)) {
    if (ISBN_13.test(v)) return 'ISBN-13';
    return 'EAN-13';
  }
  if (UPC_A.test(v)) return 'UPC-A';
  if (ITF_14.test(v)) return 'ITF-14';
  if (EAN_8.test(v) || UPC_E.test(v)) return 'EAN-8 / UPC-E';
  if (ISBN_10.test(v)) return 'ISBN-10';
  if (PURE_DIGITS.test(v)) return 'numeric';
  if (CODE_39.test(v) && v.length <= 48) return 'Code-39 / Code-128';
  return 'custom';
}

// ─── Types ─────────────────────────────────────────────────────────────────────

interface BarcodeEditorProps {
  /** Current barcodes on the product (from server). */
  barcodes: Barcode[];
  /** Called when the user presses Save. Receives the add/remove diff. */
  onSave: (payload: {
    add_barcodes?: Barcode[];
    remove_barcodes?: Barcode[];
  }) => Promise<any>;
  /** External loading flag (e.g. from parent mutation). */
  isLoading?: boolean;
  /** Store ID for cross-product barcode uniqueness check. */
  storeId?: string;
  /** Product ID being edited (to exclude self from conflict check). */
  productId?: string;
  /** Only Default barcode */
  onlyDefault?: boolean;
}

interface DraftBarcode {
  value: string;
  description: string;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const BarcodeEditor = ({
  barcodes,
  onSave,
  isLoading = false,
  storeId,
  productId,
}: BarcodeEditorProps) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const toast = useToast();

  // Server-known barcodes (source of truth from props)
  const [serverBarcodes, setServerBarcodes] = useState<Barcode[]>(
    barcodes ?? [],
  );
  // Local working copy
  const [items, setItems] = useState<Barcode[]>(barcodes ?? []);
  const [draft, setDraft] = useState<DraftBarcode>({
    value: '',
    description: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Inline editing state
  const [editingValue, setEditingValue] = useState<string | number | null>(
    null,
  );
  const [editDraft, setEditDraft] = useState<DraftBarcode>({
    value: '',
    description: '',
  });

  /**
   * Check if a barcode value is already used by another product in the same store.
   * Returns the owner entry if there's a conflict, or null if it's safe.
   */
  const checkBarcodeConflict = useCallback(
    async (value: string): Promise<{ product_name: string } | null> => {
      if (!storeId || !productId) return null;
      const owners = await productDb.findBarcodeOwner(storeId, value);
      const conflict = owners.find((o) => o.product_id !== productId);
      return conflict ? { product_name: conflict.product_name } : null;
    },
    [storeId, productId],
  );

  // Sync when parent provides new barcodes (e.g. after a refetch)
  useEffect(() => {
    setServerBarcodes(barcodes ?? []);
    setItems(barcodes ?? []);
  }, [barcodes]);

  // Compute pending diff
  const pendingChanges = useMemo(() => {
    const serverMap = new Map(
      serverBarcodes.map((b) => [String(b.value), b]),
    );
    const localMap = new Map(items.map((b) => [String(b.value), b]));

    const toAdd: Barcode[] = [];
    const toRemove: Barcode[] = [];

    // Barcodes in local but not on server, or with changed description
    for (const [val, local] of localMap) {
      const server = serverMap.get(val);
      if (!server) {
        toAdd.push(local);
      } else if (
        (local.description ?? '') !== (server.description ?? '')
      ) {
        // description changed — remove old, add new
        toRemove.push(server);
        toAdd.push(local);
      }
    }

    // Barcodes on server but removed locally
    for (const [val, server] of serverMap) {
      if (!localMap.has(val)) {
        toRemove.push(server);
      }
    }

    return { toAdd, toRemove };
  }, [items, serverBarcodes]);

  const hasPendingChanges =
    pendingChanges.toAdd.length > 0 || pendingChanges.toRemove.length > 0;

  // Add barcode locally
  const handleAdd = useCallback(async () => {
    const value = draft.value.trim();
    if (!value) {
      toast({
        title: 'Enter a barcode value',
        status: 'warning',
        duration: 2000,
      });
      return;
    }
    if (items.some((b) => String(b.value) === value)) {
      toast({
        title: 'Barcode already exists',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    // Cross-product uniqueness check
    const conflict = await checkBarcodeConflict(value);
    if (conflict) {
      toast({
        title: 'Barcode already used',
        description: `This barcode is already assigned to "${conflict.product_name}"`,
        status: 'error',
        duration: 4000,
      });
      return;
    }

    const type = inferBarcodeType(value);
    setItems((prev) => [
      ...prev,
      { value, type, description: draft.description.trim() },
    ]);
    setDraft({ value: '', description: '' });
  }, [draft, items, toast, checkBarcodeConflict]);

  // Remove barcode locally (skip default barcodes)
  const handleRemove = useCallback((value: string | number) => {
    setItems((prev) => prev.filter((b) => b.value !== value));
  }, []);

  // Start editing a barcode (skip default barcodes)
  const handleStartEdit = useCallback((bc: Barcode) => {
    if (String(bc.type) === 'default') return;
    setEditingValue(bc.value);
    setEditDraft({
      value: String(bc.value),
      description: (bc.description as string) ?? '',
    });
  }, []);

  // Confirm edit — remove old, add new
  const handleConfirmEdit = useCallback(async () => {
    const newValue = editDraft.value.trim();
    if (!newValue) {
      toast({
        title: 'Barcode value cannot be empty',
        status: 'warning',
        duration: 2000,
      });
      return;
    }
    // If value changed, make sure it doesn't collide with another barcode
    if (
      newValue !== String(editingValue) &&
      items.some((b) => String(b.value) === newValue)
    ) {
      toast({
        title: 'Another barcode already has this value',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    // Cross-product uniqueness check (only when value changed)
    if (newValue !== String(editingValue)) {
      const conflict = await checkBarcodeConflict(newValue);
      if (conflict) {
        toast({
          title: 'Barcode already used',
          description: `This barcode is already assigned to "${conflict.product_name}"`,
          status: 'error',
          duration: 4000,
        });
        return;
      }
    }

    const type = inferBarcodeType(newValue);
    setItems((prev) =>
      prev.map((b) =>
        b.value === editingValue
          ? { value: newValue, type, description: editDraft.description.trim() }
          : b,
      ),
    );
    setEditingValue(null);
  }, [editDraft, editingValue, items, toast, checkBarcodeConflict]);

  // Cancel edit
  const handleCancelEdit = useCallback(() => {
    setEditingValue(null);
  }, []);

  // Save pending changes to server
  const handleSave = useCallback(async () => {
    if (!hasPendingChanges) return;

    setIsSaving(true);
    try {
      // Final uniqueness check for all barcodes being added
      if (pendingChanges.toAdd.length > 0 && storeId && productId) {
        const addValues = pendingChanges.toAdd.map((b) => String(b.value));
        const conflicts = await productDb.getConflictsForProduct(
          storeId,
          [productId],
          addValues,
        );
        if (conflicts.length > 0) {
          const names = [...new Set(conflicts.map((c) => c.product_name))].join(
            ', ',
          );
          toast({
            title: 'Barcode conflict',
            description: `One or more barcodes are already used by: ${names}`,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          setIsSaving(false);
          return;
        }
      }

      const payload: {
        add_barcodes?: Barcode[];
        remove_barcodes?: Barcode[];
      } = {};
      if (pendingChanges.toAdd.length > 0)
        payload.add_barcodes = pendingChanges.toAdd;
      if (pendingChanges.toRemove.length > 0)
        payload.remove_barcodes = pendingChanges.toRemove;

      await onSave(payload);

      // After successful save, update server snapshot to match local
      setServerBarcodes([...items]);
      toast({
        title: 'Barcodes updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: 'Failed to update barcodes',
        description: getErrorMessage(err),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [hasPendingChanges, pendingChanges, items, onSave, toast, storeId, productId]);

  const busy = isLoading || isSaving;

  return (
    <Box mt={2} mb={1}>
      {/* Toggle header */}
      <Flex
        as="button"
        type="button"
        onClick={onToggle}
        align="center"
        w="full"
        py={1.5}
        px={2}
        bg="gray.50"
        borderRadius="md"
        _hover={{ bg: 'gray.100' }}
        transition="background 0.15s">
        <FaBarcode size={14} style={{ marginRight: 6, color: '#718096' }} />
        <Text fontSize="xs" fontWeight="600" color="gray.600">
          Barcodes
        </Text>
        {items.length > 0 && (
          <Tag size="sm" colorScheme="blue" ml={2} borderRadius="full">
            <TagLabel>{items.length}</TagLabel>
          </Tag>
        )}
        {hasPendingChanges && (
          <Tag size="sm" colorScheme="orange" ml={1} borderRadius="full">
            <TagLabel>unsaved</TagLabel>
          </Tag>
        )}
        <Box ml="auto">
          {isOpen ? (
            <FaChevronUp size={10} color="#A0AEC0" />
          ) : (
            <FaChevronDown size={10} color="#A0AEC0" />
          )}
        </Box>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <VStack align="stretch" spacing={2} pt={2} px={1}>
          {/* Existing barcodes */}
          {items.length === 0 && (
            <Text fontSize="xs" color="gray.400" textAlign="center" py={1}>
              No barcodes yet
            </Text>
          )}

          {items.map((bc) => {
            const serverMatch = serverBarcodes.find(
              (s) => String(s.value) === String(bc.value),
            );
            const isNew = !serverMatch;
            const isEdited =
              !!serverMatch &&
              (bc.description ?? '') !== (serverMatch.description ?? '');
            const isChanged = isNew || isEdited;
            const isEditing = editingValue === bc.value;
            const isDefault = String(bc.type) === 'default';

            if (isEditing) {
              return (
                <Box
                  key={`edit-${bc.value}`}
                  bg="blue.50"
                  borderWidth="1px"
                  borderColor="blue.200"
                  borderRadius="md"
                  p={2}>
                  <VStack spacing={1.5} align="stretch">
                    <Input
                      size="sm"
                      placeholder="Barcode value"
                      value={editDraft.value}
                      onChange={(e) =>
                        setEditDraft((d) => ({ ...d, value: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleConfirmEdit();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                      isDisabled={busy}
                      borderRadius="md"
                      fontSize="sm"
                      autoFocus
                    />
                    <Input
                      size="sm"
                      placeholder="Description (optional)"
                      value={editDraft.description}
                      onChange={(e) =>
                        setEditDraft((d) => ({
                          ...d,
                          description: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleConfirmEdit();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit();
                        }
                      }}
                      isDisabled={busy}
                      borderRadius="md"
                      fontSize="sm"
                    />
                    {editDraft.value.trim() && (
                      <Text fontSize="2xs" color="gray.400">
                        Type: {inferBarcodeType(editDraft.value)}
                      </Text>
                    )}
                    <HStack spacing={1} justify="flex-end">
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={handleCancelEdit}
                        isDisabled={busy}
                        fontSize="xs">
                        Cancel
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="blue"
                        leftIcon={<FaCheck size={10} />}
                        onClick={handleConfirmEdit}
                        isDisabled={busy || !editDraft.value.trim()}
                        fontSize="xs">
                        Confirm
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              );
            }

            return (
              <Flex
                key={bc.value}
                align="center"
                bg={isChanged ? 'green.50' : 'white'}
                borderWidth="1px"
                borderColor={isChanged ? 'green.200' : 'gray.200'}
                borderRadius="md"
                px={2}
                py={1.5}
                gap={2}>
                <VStack align="start" spacing={0} flex={1} minW={0}>
                  <HStack spacing={1} w="full">
                    <Text fontSize="xs" fontWeight="600" isTruncated>
                      {bc.value}
                    </Text>
                    <Tag
                      size="sm"
                      colorScheme="purple"
                      variant="subtle"
                      flexShrink={0}>
                      <TagLabel fontSize="2xs">
                        {bc.type || 'unknown'}
                      </TagLabel>
                    </Tag>
                    {isNew && (
                      <Tag
                        size="sm"
                        colorScheme="green"
                        variant="subtle"
                        flexShrink={0}>
                        <TagLabel fontSize="2xs">new</TagLabel>
                      </Tag>
                    )}
                    {isEdited && (
                      <Tag
                        size="sm"
                        colorScheme="yellow"
                        variant="subtle"
                        flexShrink={0}>
                        <TagLabel fontSize="2xs">edited</TagLabel>
                      </Tag>
                    )}
                  </HStack>
                  {bc.description && (
                    <Text fontSize="2xs" color="gray.500" isTruncated>
                      {bc.description}
                    </Text>
                  )}
                </VStack>
                <HStack spacing={0}>
                  {isDefault ? (
                    <FaLock size={12} color="#A0AEC0" title="System barcode – cannot be edited or removed" />
                  ) : (
                    <>
                      <IconButton
                        aria-label="Edit barcode"
                        icon={<FaPen />}
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        isDisabled={busy || editingValue !== null}
                        onClick={() => handleStartEdit(bc)}
                      />
                      <IconButton
                        aria-label="Remove barcode"
                        icon={<FaTimes />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        isDisabled={busy}
                        onClick={() => handleRemove(bc.value)}
                      />
                    </>
                  )}
                </HStack>
              </Flex>
            );
          })}

          {/* Add new barcode */}
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            p={2}>
            <VStack spacing={1.5} align="stretch">
              <Input
                size="sm"
                placeholder="Barcode value"
                value={draft.value}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, value: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                isDisabled={busy}
                borderRadius="md"
                fontSize="sm"
              />
              <HStack spacing={1.5}>
                <Input
                  size="sm"
                  placeholder="Description (optional)"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, description: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAdd();
                    }
                  }}
                  isDisabled={busy}
                  borderRadius="md"
                  fontSize="sm"
                  flex={1}
                />
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<FaPlus size={10} />}
                  onClick={handleAdd}
                  isDisabled={busy || !draft.value.trim()}
                  flexShrink={0}
                  fontSize="xs"
                  px={3}>
                  Add
                </Button>
              </HStack>
              {draft.value.trim() && (
                <Text fontSize="2xs" color="gray.400">
                  Type: {inferBarcodeType(draft.value)}
                </Text>
              )}
            </VStack>
          </Box>

          {/* Save button */}
          {hasPendingChanges && (
            <Button
              size="sm"
              colorScheme="green"
              leftIcon={<FaSave size={12} />}
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={busy}
              fontSize="xs"
              w="full">
              Save Barcodes
              {pendingChanges.toAdd.length > 0 &&
                ` (+${pendingChanges.toAdd.length})`}
              {pendingChanges.toRemove.length > 0 &&
                ` (-${pendingChanges.toRemove.length})`}
            </Button>
          )}
        </VStack>
      </Collapse>
    </Box>
  );
};

export default BarcodeEditor;
